import { useEffect, useRef, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useChatStore } from '../store/chat'
import { useAuthStore, API_BASE } from '../store/auth'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

// WebSocket message types
interface WSMessage {
  type: 'stream_start' | 'content' | 'stream_end' | 'error' | 'stream_stopped' | 'stream_aborted' | 'ping' | 'pong' | 'auth_success' | 'auth_error'
  content?: string
  conversation_id?: string
  message_id?: string
  error?: string
  code?: string
  timestamp?: number
}

interface ChatDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export default function ChatDrawer({ isOpen, onClose }: ChatDrawerProps) {
  const { t } = useTranslation()
  const { activeOrg } = useAuthStore()
  const { 
    messages: storedMessages, 
    conversationId, 
    setConversationId,
    input,
    setInput,
    loading,
    setLoading,
    streamingContent,
    setStreamingContent,
    appendStreamingContent,
    addMessage,
    clearMessages,
  } = useChatStore()
  
  const [error, setError] = useState('')
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>('disconnected')

  // After this many ms of no drawer activity, disconnect to let the DO hibernate
  const IDLE_TIMEOUT_MS = 10 * 60 * 1000

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>()
  const pingIntervalRef = useRef<NodeJS.Timeout>()
  const idleTimeoutRef = useRef<NodeJS.Timeout>()
  // True when we closed the socket on purpose — suppresses auto-reconnect even if
  // the browser reports code 1006 (happens when the server is hibernating and
  // doesn't respond to our close frame before the TCP connection drops).
  const intentionalCloseRef = useRef(false)
  const currentMessageIdRef = useRef<string>('')
  const streamingContentRef = useRef<string>('')

  // Convert stored messages to Message objects with Date
  const messages: Message[] = storedMessages.map(m => ({
    ...m,
    timestamp: new Date(m.timestamp)
  }))

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [storedMessages, streamingContent])

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`
    }
  }, [input])

  // Focus textarea when drawer opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100)
    }
  }, [isOpen])

  // Close connection cleanly so the DO can hibernate — no auto-reconnect
  const disconnectIdle = useCallback(() => {
    clearInterval(pingIntervalRef.current)
    clearTimeout(reconnectTimeoutRef.current)
    clearTimeout(idleTimeoutRef.current)
    if (wsRef.current) {
      intentionalCloseRef.current = true
      wsRef.current.close(1000, 'Idle disconnect')
      wsRef.current = null
    }
    setConnectionStatus('disconnected')
  }, [])

  // Reset the idle countdown — call on any user interaction in the drawer
  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimeoutRef.current)
    idleTimeoutRef.current = setTimeout(disconnectIdle, IDLE_TIMEOUT_MS)
  }, [disconnectIdle, IDLE_TIMEOUT_MS])

  // Connect WebSocket
  const connectWebSocket = useCallback(() => {
    if (!activeOrg) return
    // Bail if already open or in the middle of connecting (avoids duplicate sockets)
    const state = wsRef.current?.readyState
    if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return

    setConnectionStatus('connecting')

    // Include organizationId as query parameter (required by backend)
    const wsUrl = API_BASE.replace(/^http/, 'ws') + '/chat/stream?organizationId=' + encodeURIComponent(activeOrg.id)

    try {
      const ws = new WebSocket(wsUrl)
      
      ws.onopen = () => {
        setConnectionStatus('connected')
        setError('')
        // Ping every 25s to stay under the 30s server heartbeat interval
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }))
          }
        }, 25000)
        // Start idle countdown on connect
        resetIdleTimer()
      }

      ws.onmessage = (event) => {
        try {
          const data: WSMessage = JSON.parse(event.data)
          handleWebSocketMessage(data)
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e)
        }
      }

      ws.onclose = (event) => {
        clearInterval(pingIntervalRef.current)
        setConnectionStatus('disconnected')
        wsRef.current = null

        console.log(`WebSocket closed: code=${event.code}, reason=${event.reason}`)

        // Skip reconnect when we closed intentionally (idle disconnect, tab hidden,
        // or component unmounting). We also check code 1000 as a belt-and-suspenders
        // guard; the intentional flag handles cases where the server is hibernating
        // and doesn't answer our close frame, causing the browser to report 1006.
        const wasIntentional = intentionalCloseRef.current
        intentionalCloseRef.current = false
        if (!wasIntentional && event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (activeOrg) {
              connectWebSocket()
            }
          }, 3000)
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        setConnectionStatus('disconnected')
        setError('Yhteys katkesi. Yritetään yhdistää uudelleen...')
      }

      wsRef.current = ws
    } catch {
      setConnectionStatus('disconnected')
      setError('WebSocket-yhteyden muodostaminen epäonnistui')
    }
  }, [activeOrg, resetIdleTimer])

  // Reconnect on any user interaction if the connection is dead, and reset idle timer
  const handleActivity = useCallback(() => {
    resetIdleTimer()
    if (activeOrg && (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) {
      connectWebSocket()
    }
  }, [resetIdleTimer, activeOrg, connectWebSocket])

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data: WSMessage) => {
    switch (data.type) {
      case 'auth_success':
        // Connection authenticated successfully, ready for chat
        console.log('WebSocket authenticated successfully')
        break

      case 'auth_error':
        setError(data.error || 'Authentication failed')
        setLoading(false)
        break

      case 'ping':
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'pong' }))
        }
        break

      case 'pong':
        // Server heartbeat response, connection is alive
        break

      case 'stream_start':
        if (data.conversation_id && !conversationId) {
          setConversationId(data.conversation_id)
        }
        setStreamingContent('')
        streamingContentRef.current = ''
        break

      case 'content':
        if (data.content) {
          appendStreamingContent(data.content)
          streamingContentRef.current += data.content
        }
        break

      case 'stream_end':
        if (streamingContentRef.current || currentMessageIdRef.current) {
          const assistantMessage = {
            id: data.message_id || currentMessageIdRef.current || Date.now().toString(),
            role: 'assistant' as const,
            content: streamingContentRef.current,
            timestamp: new Date().toISOString(),
          }
          addMessage(assistantMessage)
          setStreamingContent('')
          streamingContentRef.current = ''
          setLoading(false)
          currentMessageIdRef.current = ''
        }
        break

      case 'stream_stopped':
      case 'stream_aborted':
        setLoading(false)
        setStreamingContent('')
        streamingContentRef.current = ''
        currentMessageIdRef.current = ''
        break

      case 'error':
        setError(data.error || t('chat.messageError'))
        setLoading(false)
        break
    }
  }, [conversationId, setConversationId, setStreamingContent, appendStreamingContent, addMessage, setLoading])

  // Connect on mount when drawer is open
  useEffect(() => {
    if (isOpen && activeOrg) {
      connectWebSocket()
    }
    
    return () => {
      clearInterval(pingIntervalRef.current)
      clearTimeout(reconnectTimeoutRef.current)
      clearTimeout(idleTimeoutRef.current)
      if (wsRef.current) {
        intentionalCloseRef.current = true
        wsRef.current.close(1000, 'Component unmounting')
      }
    }
  }, [isOpen, activeOrg, connectWebSocket])

  // Disconnect when tab is hidden; reconnect (lazily via handleActivity) when visible again
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        disconnectIdle()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [disconnectIdle])

  const sendMessage = async () => {
    if (!input.trim() || !activeOrg) return
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError('Yhteys katkesi. Yhdistetään uudelleen...')
      connectWebSocket()
      return
    }

    const userMessage = {
      id: Date.now().toString(),
      role: 'user' as const,
      content: input.trim(),
      timestamp: new Date().toISOString(),
    }

    addMessage(userMessage)
    setInput('')
    setLoading(true)
    setError('')
    setStreamingContent('')

    wsRef.current.send(JSON.stringify({
      type: 'chat_message',
      message: userMessage.content,
      organization_id: activeOrg.id,
      conversation_id: conversationId || undefined,
    }))
  }

  const stopStreaming = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN && loading) {
      wsRef.current.send(JSON.stringify({ type: 'stop' }))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const handleClear = () => {
    clearMessages()
    setError('')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer panel */}
      <div
        className="relative w-full max-w-md bg-white shadow-2xl flex flex-col h-full animate-slide-in-right"
        onPointerDown={handleActivity}
        onKeyDown={handleActivity}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-indigo-600 text-white">
          <div className="flex items-center gap-2">
            <span className="text-xl">🤖</span>
            <div>
              <h2 className="font-semibold text-sm">AI-kirjanpitäjä</h2>
              <div className="flex items-center gap-1.5 text-xs text-indigo-200">
                <span className={`w-1.5 h-1.5 rounded-full ${
                  connectionStatus === 'connected' ? 'bg-green-400' :
                  connectionStatus === 'connecting' ? 'bg-yellow-400 animate-pulse' :
                  'bg-red-400'
                }`} />
                <span>
                  {connectionStatus === 'connected' ? 'Yhdistetty' :
                   connectionStatus === 'connecting' ? 'Yhdistetään...' :
                   'Ei yhteyttä'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button
                onClick={handleClear}
                className="text-xs text-indigo-200 hover:text-white transition-colors px-2 py-1 rounded hover:bg-indigo-500"
                title="Tyhjennä keskustelu"
              >
                🗑️ Tyhjennä
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-indigo-500 rounded-lg transition-colors"
              aria-label="Sulje chat"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="px-4 py-2 bg-red-50 border-b border-red-100">
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">💬</div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">
                Tervetuloa AI-chattiin!
              </h3>
              <p className="text-xs text-gray-600 max-w-xs mx-auto mb-4">
                Voit kysyä kirjanpitäjältä mitä tahansa kirjanpitoon liittyen.
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• "Mitä kuuluu tällä hetkellä?"</li>
                <li>• "{t('chat.exampleIncome')}"</li>
                <li>• "Kategorisoi viimeisimmät tapahtumat"</li>
                <li>• "{t('chat.exampleExpense')}"</li>
              </ul>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-md'
                      : 'bg-white text-gray-900 rounded-bl-md shadow-sm border border-gray-200'
                  }`}
                >
                  <div className="whitespace-pre-wrap">{message.content}</div>
                  <div className={`text-xs mt-1 ${
                    message.role === 'user' ? 'text-blue-200' : 'text-gray-400'
                  }`}>
                    {message.timestamp.toLocaleTimeString('fi-FI', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
          
          {/* Streaming message */}
          {streamingContent && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl rounded-bl-md px-3 py-2 text-sm bg-white text-gray-900 shadow-sm border border-gray-200">
                <div className="whitespace-pre-wrap">
                  {streamingContent}
                  <span className="inline-block w-1.5 h-3.5 bg-blue-500 ml-1 animate-pulse" />
                </div>
              </div>
            </div>
          )}

          {/* Loading indicator */}
          {loading && !streamingContent && (
            <div className="flex justify-start">
              <div className="bg-white rounded-2xl rounded-bl-md px-3 py-2 shadow-sm border border-gray-200">
                <div className="flex items-center space-x-1.5">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-100" />
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-200" />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 bg-white p-3">
          <div className="flex items-end space-x-2">
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Kirjoita viesti..."
                disabled={loading}
                rows={1}
                className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
              />
              <div className="absolute right-2 bottom-2 text-xs text-gray-400">
                {input.length > 0 && input.length}
              </div>
            </div>
            {loading ? (
              <button
                onClick={stopStreaming}
                className="bg-red-600 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-red-700 transition-colors"
              >
                ⏹️
              </button>
            ) : (
              <button
                onClick={sendMessage}
                disabled={!input.trim() || !activeOrg || connectionStatus === 'connecting'}
                className="bg-blue-600 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                ➤
              </button>
            )}
          </div>
          <p className="text-center text-xs text-gray-400 mt-1.5">
            Enter lähettää, Shift+Enter uudelle riville
          </p>
        </div>
      </div>
    </div>
  )
}
